import { useEffect, useState } from 'react';

const statusOptions = [
  { value: 'available', label: 'Available' },
  { value: 'blocked', label: 'Blocked' },
];

const DayEditorDrawer = ({ open, date, day, onClose, onSave, onClear }) => {
  const [status, setStatus] = useState(day?.status || 'available');
  const [price, setPrice] = useState(day?.price_override ?? day?.price ?? '');
  const [minNights, setMinNights] = useState(day?.min_nights_override ?? day?.min_nights ?? day?.minNights ?? '');
  const [note, setNote] = useState(day?.note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setStatus(day?.status || 'available');
      setPrice(day?.price_override ?? day?.price ?? '');
      setMinNights(day?.min_nights_override ?? day?.min_nights ?? day?.minNights ?? '');
      setNote(day?.note || '');
      setError('');
    }
  }, [open, day]);

  const handleSave = async () => {
    if (day?.status === 'booked') {
      setError('Booked dates cannot be edited');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave?.({
        status,
        price_override: price,
        min_nights_override: minNights,
        note,
      });
    } catch (err) {
      setError(err.message || 'Failed to save day');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30">
      <div className="h-full w-full max-w-md bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-sm font-semibold text-slate-900">Edit {date}</div>
            <div className="text-xs text-slate-500">
              {day?.status === 'booked' ? 'Booked (read only)' : 'Update status, price, or notes'}
            </div>
          </div>
          <button
            type="button"
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100"
            onClick={() => onClose?.()}
          >
            X
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="text-xs font-semibold text-slate-600">Status</label>
            <select
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={day?.status === 'booked'}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Price override</label>
            <input
              type="number"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Leave blank to use base pricing"
              disabled={day?.status === 'booked'}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Min nights override</label>
            <input
              type="number"
              min="1"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={minNights}
              onChange={(e) => setMinNights(e.target.value)}
              placeholder="e.g., 2"
              disabled={day?.status === 'booked'}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Note</label>
            <textarea
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note for this date"
            />
          </div>
          {error && <div className="text-sm text-rose-600">{error}</div>}
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => onClear?.()}
            disabled={day?.status === 'booked' || saving}
          >
            Clear overrides
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => onClose?.()}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
              onClick={handleSave}
              disabled={saving || day?.status === 'booked'}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayEditorDrawer;
